// Theme Configuration for ZippyHealth
// Centralized color system for consistent light/dark mode support

export const theme = {
  colors: {
    // Background colors
    background: {
      primary: 'bg-white dark:bg-slate-900',
      secondary: 'bg-slate-50 dark:bg-slate-800',
      tertiary: 'bg-slate-100 dark:bg-slate-700',
      card: 'bg-white dark:bg-slate-800',
      overlay: 'bg-black/50 dark:bg-black/70',
    },
    
    // Text colors
    text: {
      primary: 'text-slate-900 dark:text-white',
      secondary: 'text-slate-700 dark:text-slate-300',
      tertiary: 'text-slate-600 dark:text-slate-400',
      muted: 'text-slate-500 dark:text-slate-500',
      inverse: 'text-white dark:text-slate-900',
    },
    
    // Border colors
    border: {
      primary: 'border-slate-200 dark:border-slate-700',
      secondary: 'border-slate-300 dark:border-slate-600',
      accent: 'border-blue-200 dark:border-blue-700',
    },
    
    // Interactive states
    interactive: {
      hover: 'hover:bg-slate-50 dark:hover:bg-slate-700',
      active: 'bg-slate-100 dark:bg-slate-600',
      focus: 'focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400',
    },
    
    // Status colors with dark mode variants
    status: {
      success: {
        bg: 'bg-green-50 dark:bg-green-900/20',
        text: 'text-green-700 dark:text-green-300',
        border: 'border-green-200 dark:border-green-800',
        icon: 'text-green-600 dark:text-green-400',
      },
      warning: {
        bg: 'bg-yellow-50 dark:bg-yellow-900/20',
        text: 'text-yellow-700 dark:text-yellow-300',
        border: 'border-yellow-200 dark:border-yellow-800',
        icon: 'text-yellow-600 dark:text-yellow-400',
      },
      error: {
        bg: 'bg-red-50 dark:bg-red-900/20',
        text: 'text-red-700 dark:text-red-300',
        border: 'border-red-200 dark:border-red-800',
        icon: 'text-red-600 dark:text-red-400',
      },
      info: {
        bg: 'bg-blue-50 dark:bg-blue-900/20',
        text: 'text-blue-700 dark:text-blue-300',
        border: 'border-blue-200 dark:border-blue-800',
        icon: 'text-blue-600 dark:text-blue-400',
      },
    },
    
    // Role-specific colors
    roles: {
      super_admin: {
        bg: 'bg-red-50 dark:bg-red-900/20',
        text: 'text-red-700 dark:text-red-300',
        border: 'border-red-200 dark:border-red-800',
        icon: 'text-red-600 dark:text-red-400',
      },
      healthcare_provider: {
        bg: 'bg-green-50 dark:bg-green-900/20',
        text: 'text-green-700 dark:text-green-300',
        border: 'border-green-200 dark:border-green-800',
        icon: 'text-green-600 dark:text-green-400',
      },
      lab_technician: {
        bg: 'bg-purple-50 dark:bg-purple-900/20',
        text: 'text-purple-700 dark:text-purple-300',
        border: 'border-purple-200 dark:border-purple-800',
        icon: 'text-purple-600 dark:text-purple-400',
      },
      guru: {
        bg: 'bg-blue-50 dark:bg-blue-900/20',
        text: 'text-blue-700 dark:text-blue-300',
        border: 'border-blue-200 dark:border-blue-800',
        icon: 'text-blue-600 dark:text-blue-400',
      },
      demo: {
        bg: 'bg-slate-50 dark:bg-slate-800/50',
        text: 'text-slate-700 dark:text-slate-300',
        border: 'border-slate-200 dark:border-slate-700',
        icon: 'text-slate-600 dark:text-slate-400',
      },
    },
  },
  
  // Component-specific theme classes
  components: {
    card: {
      base: 'bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-slate-200 dark:border-slate-700',
      hover: 'hover:shadow-md dark:hover:shadow-lg',
      interactive: 'hover:bg-slate-50 dark:hover:bg-slate-700',
    },
    
    button: {
      primary: 'bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white',
      secondary: 'bg-slate-100 hover:bg-slate-200 dark:bg-slate-700 dark:hover:bg-slate-600 text-slate-900 dark:text-white',
      outline: 'border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700',
    },
    
    input: {
      base: 'bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 text-slate-900 dark:text-white placeholder-slate-500 dark:placeholder-slate-400',
      focus: 'focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400',
    },
    
    badge: {
      base: 'inline-flex items-center rounded-full font-medium',
      variants: {
        default: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
        secondary: 'bg-slate-100 text-slate-800 dark:bg-slate-700 dark:text-slate-300',
        success: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
        warning: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
        error: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
      },
    },
  },
};

// Utility functions for theme classes
export const getThemeClass = (category: keyof typeof theme.colors, variant: string) => {
  const colorCategory = theme.colors[category];
  return colorCategory[variant as keyof typeof colorCategory] || '';
};

export const getComponentClass = (component: keyof typeof theme.components, variant?: string) => {
  const componentTheme = theme.components[component];
  if (variant && componentTheme[variant as keyof typeof componentTheme]) {
    return componentTheme[variant as keyof typeof componentTheme];
  }
  return componentTheme.base || '';
};

// Role-specific theme helper
export const getRoleTheme = (role: string) => {
  const roleKey = role as keyof typeof theme.colors.roles;
  return theme.colors.roles[roleKey] || theme.colors.roles.demo;
};

// Status-specific theme helper
export const getStatusTheme = (status: string) => {
  const statusKey = status as keyof typeof theme.colors.status;
  return theme.colors.status[statusKey] || theme.colors.status.info;
}; 